import { motion, type Variants } from 'framer-motion'
import type { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'

const LOADER_CONFIG = {
  ROTATION_DURATION: 10,
  STAGGER_DELAY: 0.12,
  ITEM_DURATION: 1.2,
  INITIAL_OPACITY: 0.3,
  MAX_OPACITY: 1,
  INITIAL_SCALE: 0.7,
  MAX_SCALE: 1.1,
  ANIM_TITLE_DURATION: 0.5,
  DAMPING_Y: 5,
  TITLE_OPACITY: 0.8,
  ITEM_COUNT: 4,
} as const

const LOADER_ITEMS = Array.from({ length: LOADER_CONFIG.ITEM_COUNT }, (_, i) => i)

export function RouteLoader(): ReactElement {
  const { t } = useTranslation('layout')
  const containerVariants: Variants = {
    animate: {
      rotate: 360,
      transition: {
        rotate: {
          duration: LOADER_CONFIG.ROTATION_DURATION,
          repeat: Infinity,
          ease: 'linear',
        },
        staggerChildren: LOADER_CONFIG.STAGGER_DELAY,
      },
    },
  }

  const itemVariants: Variants = {
    initial: {
      opacity: LOADER_CONFIG.INITIAL_OPACITY,
      scale: LOADER_CONFIG.INITIAL_SCALE,
    },
    animate: {
      opacity: [
        LOADER_CONFIG.INITIAL_OPACITY,
        LOADER_CONFIG.MAX_OPACITY,
        LOADER_CONFIG.INITIAL_OPACITY,
      ],
      scale: [LOADER_CONFIG.INITIAL_SCALE, LOADER_CONFIG.MAX_SCALE, LOADER_CONFIG.INITIAL_SCALE],
      transition: {
        duration: LOADER_CONFIG.ITEM_DURATION,
        repeat: Infinity,
        ease: 'easeInOut',
      },
    },
  }

  return (
    <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-bg-base overflow-hidden">
      <motion.div
        variants={containerVariants}
        initial="initial"
        animate="animate"
        className="grid grid-cols-2 gap-2 mb-10"
      >
        {LOADER_ITEMS.map((i) => (
          <motion.div
            key={i}
            variants={itemVariants}
            className="h-7 w-7 rounded-[5px] bg-primary shadow-[0_0_15px_-3px_rgba(var(--primary-rgb),0.3)]"
          />
        ))}
      </motion.div>

      <div className="text-center flex flex-col gap-2 select-none">
        <motion.h3
          initial={{ opacity: 0, y: LOADER_CONFIG.DAMPING_Y }}
          animate={{ opacity: LOADER_CONFIG.TITLE_OPACITY, y: 0 }}
          transition={{ duration: LOADER_CONFIG.ANIM_TITLE_DURATION }}
          className="text-xs font-semibold tracking-[0.2em] text-text-primary uppercase"
        >
          {t('shell.title')}
        </motion.h3>
        <p className="text-[10px] font-medium text-text-tertiary tracking-widest uppercase opacity-60">
          {t('routeLoader.label')}
        </p>
      </div>
    </div>
  )
}
